import { HttpClient, HttpStatusCode } from 'data/protocols/http';
import { generateUrl } from 'data/utils';
import { InvalidCredentialsError, UnexpectedError } from 'domain/errors';
import {
  GetProfessionalsWorkJourneyUseCaseInterface,
  HandleProfessionalsWorkJourneyUseCaseInterface,
} from 'domain/usecases';

export class RemoteGetProfessionalsWorkJourney
  implements GetProfessionalsWorkJourneyUseCaseInterface
{
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient<GetProfessionalsWorkJourneyUseCaseInterface.Result>,
  ) {}

  async execute(
    params: GetProfessionalsWorkJourneyUseCaseInterface.Params,
  ): Promise<GetProfessionalsWorkJourneyUseCaseInterface.Result> {
    const httpResponse = await this.httpClient.request({
      method: 'get',
      url: generateUrl(this.url, params),
      responseType: 'json',
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.forbidden:
        throw new InvalidCredentialsError();
      default:
        throw new UnexpectedError(httpResponse.body.message);
    }
  }
}

export class RemoteHandleProfessionalsWorkJourney
  implements HandleProfessionalsWorkJourneyUseCaseInterface
{
  private readonly pdfUrl: string;
  private readonly excelUrl?: string;
  private readonly httpClient: HttpClient<HandleProfessionalsWorkJourneyUseCaseInterface.Result>;

  constructor(
    pdfUrl: string,
    excelUrlOrHttpClient:
      | string
      | HttpClient<HandleProfessionalsWorkJourneyUseCaseInterface.Result>,
    httpClient?: HttpClient<HandleProfessionalsWorkJourneyUseCaseInterface.Result>,
  ) {
    this.pdfUrl = pdfUrl;

    if (typeof excelUrlOrHttpClient === 'string') {
      this.excelUrl = excelUrlOrHttpClient;
      this.httpClient =
        httpClient as HttpClient<HandleProfessionalsWorkJourneyUseCaseInterface.Result>;
    } else {
      this.httpClient = excelUrlOrHttpClient;
    }
  }

  async execute(
    params: HandleProfessionalsWorkJourneyUseCaseInterface.Params,
  ): Promise<HandleProfessionalsWorkJourneyUseCaseInterface.Result> {
    const { type, ...restParams } = params;
    const requestUrl = this.excelUrl
      ? generateUrl(type === 'pdf' ? this.pdfUrl : this.excelUrl, restParams)
      : generateUrl(this.pdfUrl, params);

    const httpResponse = await this.httpClient.request({
      method: 'get',
      url: requestUrl,
      responseType: 'json',
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.forbidden:
        throw new InvalidCredentialsError();
      default:
        throw new UnexpectedError(httpResponse.body.message);
    }
  }
}
